import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo.jsx";

/* AirSpace Live — privacy policy.
   Mounted at /airspace-live-app-privacy as its own route. Self-contained
   tactical theme (.aslp-* namespace) matching the marketing page. Plain,
   honest disclosure required by the Chrome Web Store: the only data the
   extension touches is the postcode the user types, which is sent to the
   aircraft data services to center the radar. Nothing is stored. */

const APP_NAME = "AirSpace Live";
const LAST_UPDATED = "June 16, 2026";

const THIRD_PARTIES = [
  { name: "Nominatim (OpenStreetMap)", use: "turns your postcode into map coordinates" },
  { name: "airplanes.live", use: "returns aircraft currently flying near those coordinates" },
  { name: "planespotters.net", use: "provides aircraft photos and details" },
];

function AirSpaceLivePrivacyScreen() {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="aslp-page screen-enter">
      <Seo
        title={`${APP_NAME} — Privacy Policy`}
        description={`Privacy policy for the ${APP_NAME} Chrome extension. The only data we use is the postcode you enter, sent to aircraft data services to center the radar. Nothing is stored or sold.`}
        keywords={["airspace live privacy policy", "aircraft radar extension privacy"]}
        noindex
      />

      <style>{ASLP_CSS}</style>

      <header className="aslp-top">
        <button
          type="button"
          className="aslp-back"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/airspace-live-app");
          }}
          aria-label="Go back"
        >
          <window.I.ArrowLeft size={14} />
          <span>Back</span>
        </button>
        <Link to="/airspace-live-app" className="aslp-brand" aria-label={`${APP_NAME} home`}>
          <span className="aslp-brand-mark" aria-hidden="true" />
          <span className="aslp-brand-name">{APP_NAME}</span>
        </Link>
      </header>

      <main className="aslp-shell">
        <span className="aslp-eyebrow">
          <span className="aslp-eyebrow-dot" /> Privacy Policy
        </span>
        <h1 className="aslp-title">{APP_NAME} Privacy Policy</h1>
        <p className="aslp-updated">Last updated: {LAST_UPDATED}</p>

        <p className="aslp-lede">
          {APP_NAME} is built to do one thing: show you the aircraft flying
          overhead. We keep it simple, and we keep your data on your device. We
          do not collect accounts, we do not track you, and we do not store or
          sell any personal information.
        </p>

        <section className="aslp-section">
          <h2 className="aslp-h2">What we use</h2>
          <p>
            The only piece of information the extension uses is the{" "}
            <strong>postcode you type in</strong>. We use it to center the radar
            map on your area. We don't collect your name, email, account
            details, browsing history, or the pages you visit. Clicks on radar
            blips stay inside the page — nothing is logged or sent anywhere.
          </p>
        </section>

        <section className="aslp-section">
          <h2 className="aslp-h2">Where your postcode goes</h2>
          <p>
            To show real aircraft, your postcode (and the coordinates it maps
            to) is sent to the following third-party services purely to fetch
            nearby flights. This is a necessary part of the feature:
          </p>
          <ul className="aslp-list">
            {THIRD_PARTIES.map((t) => (
              <li key={t.name}>
                <strong>{t.name}</strong> — {t.use}.
              </li>
            ))}
          </ul>
          <p>
            We don't add any identifying information to these requests, and we
            don't keep a copy of your postcode after the radar loads.
          </p>
        </section>

        <section className="aslp-section">
          <h2 className="aslp-h2">What we don't do</h2>
          <ul className="aslp-list">
            <li>We don't sell or share your data with anyone for advertising.</li>
            <li>We don't store your location or any history on our servers.</li>
            <li>We don't use your data for anything beyond showing aircraft.</li>
            <li>We don't use your data for creditworthiness or lending.</li>
          </ul>
        </section>

        <section className="aslp-section">
          <h2 className="aslp-h2">Contact</h2>
          <p>
            Questions about this policy? Reach out at{" "}
            <a className="aslp-link" href="mailto:hello@communityninja.io">
              hello@communityninja.io
            </a>
            .
          </p>
        </section>
      </main>

      <footer className="aslp-foot">
        <span className="aslp-brand-mark" aria-hidden="true" />
        <span>{APP_NAME}</span>
        <Link to="/airspace-live-app" className="aslp-foot-link">
          Back to home
        </Link>
      </footer>
    </div>
  );
}

const ASLP_CSS = `
.aslp-page {
  --aslp-bg: #070a07;
  --aslp-panel: #0d130c;
  --aslp-line: #1d2a18;
  --aslp-green: #6f9e3f;
  --aslp-green-bright: #9ee35a;
  --aslp-text: #d6e2c8;
  --aslp-muted: #7c8a6c;
  min-height: 100vh;
  background:
    radial-gradient(1000px 500px at 70% -10%, rgba(111,158,63,0.08), transparent 60%),
    var(--aslp-bg);
  color: var(--aslp-text);
  font-family: "JetBrains Mono", "SFMono-Regular", ui-monospace, Menlo, monospace;
}
.aslp-top {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; border-bottom: 1px solid var(--aslp-line);
}
.aslp-back {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: 1px solid var(--aslp-line); border-radius: 6px;
  color: var(--aslp-muted); padding: 6px 12px; cursor: pointer;
  font: inherit; font-size: 12px; letter-spacing: 0.04em;
  transition: border-color .15s, color .15s;
}
.aslp-back:hover { color: var(--aslp-green-bright); border-color: var(--aslp-green); }
.aslp-brand { display: inline-flex; align-items: center; gap: 9px; text-decoration: none; }
.aslp-brand-name {
  font-size: 13px; letter-spacing: 0.18em; text-transform: uppercase;
  color: var(--aslp-green-bright); font-weight: 600;
}
.aslp-brand-mark {
  width: 10px; height: 10px; border-radius: 50%;
  background: var(--aslp-green-bright);
  box-shadow: 0 0 10px 2px rgba(158,227,90,0.6);
}
.aslp-shell { max-width: 720px; margin: 0 auto; padding: 56px 24px 72px; }
.aslp-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; letter-spacing: 0.22em; text-transform: uppercase;
  color: var(--aslp-green); margin-bottom: 18px;
}
.aslp-eyebrow-dot {
  width: 7px; height: 7px; border-radius: 50%; background: var(--aslp-green-bright);
  box-shadow: 0 0 8px rgba(158,227,90,0.7);
}
.aslp-title { font-size: 32px; line-height: 1.15; margin: 0; font-weight: 700; color: #eef4e4; }
.aslp-updated { margin: 10px 0 0; font-size: 12px; letter-spacing: 0.06em; color: var(--aslp-muted); }
.aslp-lede { margin: 26px 0 0; font-size: 14.5px; line-height: 1.65; color: var(--aslp-text); }
.aslp-section { margin-top: 34px; }
.aslp-h2 { font-size: 16px; color: var(--aslp-green-bright); margin: 0 0 12px; font-weight: 600; letter-spacing: 0.02em; }
.aslp-section p { margin: 0 0 12px; font-size: 14px; line-height: 1.65; color: var(--aslp-muted); }
.aslp-section p strong, .aslp-list strong { color: var(--aslp-text); }
.aslp-list { margin: 0; padding-left: 18px; }
.aslp-list li { font-size: 14px; line-height: 1.65; color: var(--aslp-muted); margin-bottom: 8px; }
.aslp-link { color: var(--aslp-green-bright); text-decoration: underline; }
.aslp-foot {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  padding: 28px 24px; border-top: 1px solid var(--aslp-line);
  font-size: 12px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--aslp-muted);
}
.aslp-foot-link { color: var(--aslp-green); text-decoration: none; }
.aslp-foot-link:hover { color: var(--aslp-green-bright); }

@media (max-width: 600px) {
  .aslp-title { font-size: 26px; }
}
`;

window.AirSpaceLivePrivacyScreen = AirSpaceLivePrivacyScreen;

export default AirSpaceLivePrivacyScreen;
